import {useEffect,useCallback} from 'react';
import {useReactFlow} from 'reactflow';
import {nanoid} from 'nanoid';

const isMac=()=>navigator.userAgent.toUpperCase().includes('MAC');

/**
 * useKeyboardShortcuts
 * Covers: Delete, Copy/Paste (system clipboard + edges), Duplicate,
 *         Select All, Undo/Redo, Zoom, Arrow nudge (1px / 10px w/ Shift),
 *         Group (⌘G) / Ungroup (⌘Shift+G).
 */
export default function useKeyboardShortcuts({
    nodes,
    edges,
    setNodes,
    setEdges,
    readOnly=false,
    onDelete,
    onUndo,
    onRedo,
    onGroup,
    onUngroup,
    disabled=false
}) {
    const {setViewport,getViewport}=useReactFlow();

    const getSelectedElements=useCallback(()=>({
        selectedNodes: nodes.filter(n=>n.selected),
        selectedEdges: edges.filter(e=>e.selected)
    }),[nodes,edges]);

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete=useCallback(()=>{
        if(readOnly||disabled) return;
        const {selectedNodes,selectedEdges}=getSelectedElements();
        if(!selectedNodes.length&&!selectedEdges.length) return;
        const ids=new Set(selectedNodes.map(n=>n.id));
        setNodes(ns=>ns.filter(n=>!n.selected));
        setEdges(es=>es.filter(e=>!e.selected&&!ids.has(e.source)&&!ids.has(e.target)));
        onDelete?.(selectedNodes,selectedEdges);
    },[getSelectedElements,setNodes,setEdges,onDelete,readOnly,disabled]);

    // ── Copy — system clipboard with node+edge payload ───────────────────────
    const handleCopy=useCallback(async ()=>{
        if(disabled) return false;
        const {selectedNodes}=getSelectedElements();
        if(!selectedNodes.length) return false;
        const selectedIds=new Set(selectedNodes.map(n=>n.id));
        const internalEdges=edges.filter(e=>selectedIds.has(e.source)&&selectedIds.has(e.target));
        const payload=JSON.stringify({__flowsage: true,nodes: selectedNodes,edges: internalEdges});
        try {
            await navigator.clipboard.writeText(payload);
        } catch {
            localStorage.setItem('flowsage_clipboard',payload);
        }
        return true;
    },[getSelectedElements,edges,disabled]);

    // ── Paste — system clipboard with full ID remap (nodes + edges) ───────────
    const handlePaste=useCallback(async ()=>{
        if(readOnly||disabled) return;
        let raw=null;
        try {raw=await navigator.clipboard.readText();} catch {raw=localStorage.getItem('flowsage_clipboard');}
        if(!raw) return;
        try {
            const data=JSON.parse(raw);
            if(!data.__flowsage||!data.nodes?.length) return;
            const idMap={};
            const newNodes=data.nodes.map(node=>{
                const newId=nanoid();
                idMap[node.id]=newId;
                return {...node,id: newId,position:{x:node.position.x+50,y:node.position.y+50},selected:true,data:{...node.data,approved:true}};
            });
            const newEdges=(data.edges||[]).map(edge=>({
                ...edge,id:nanoid(),source:idMap[edge.source]||edge.source,target:idMap[edge.target]||edge.target
            }));
            setNodes(ns=>ns.map(n=>({...n,selected:false})).concat(newNodes));
            setEdges(es=>[...es,...newEdges]);
        } catch {/* ignore */}
    },[setNodes,setEdges,readOnly,disabled]);

    // ── Duplicate ─────────────────────────────────────────────────────────────
    const handleDuplicate=useCallback(()=>{
        if(readOnly||disabled) return;
        const {selectedNodes}=getSelectedElements();
        if(!selectedNodes.length) return;
        const selectedIds=new Set(selectedNodes.map(n=>n.id));
        const internalEdges=edges.filter(e=>selectedIds.has(e.source)&&selectedIds.has(e.target));
        const idMap={};
        const newNodes=selectedNodes.map(node=>{
            const newId=nanoid();
            idMap[node.id]=newId;
            return {...node,id:newId,position:{x:node.position.x+50,y:node.position.y+50},selected:false,data:{...node.data,approved:true}};
        });
        const newEdges=internalEdges.map(edge=>({
            ...edge,id:nanoid(),source:idMap[edge.source]||edge.source,target:idMap[edge.target]||edge.target
        }));
        setNodes(ns=>[...ns,...newNodes]);
        setEdges(es=>[...es,...newEdges]);
    },[getSelectedElements,edges,setNodes,setEdges,readOnly,disabled]);

    // ── Select All ────────────────────────────────────────────────────────────
    const handleSelectAll=useCallback(()=>{
        if(disabled) return;
        setNodes(ns=>ns.map(n=>({...n,selected:true})));
        setEdges(es=>es.map(e=>({...e,selected:true})));
    },[setNodes,setEdges,disabled]);

    // ── Zoom ──────────────────────────────────────────────────────────────────
    const handleZoom=useCallback((dir)=>{
        if(disabled) return;
        const vp=getViewport();
        const newZoom=dir==='in'? Math.min(vp.zoom+0.1,2):Math.max(vp.zoom-0.1,0.1);
        setViewport({...vp,zoom:newZoom});
    },[getViewport,setViewport,disabled]);

    // ── Arrow nudge: 1px default, 10px with Shift ─────────────────────────────
    const handleArrowKey=useCallback((key,shift)=>{
        if(readOnly||disabled) return;
        const {selectedNodes}=getSelectedElements();
        if(!selectedNodes.length) return;
        const step=shift? 10:1;
        const delta={ArrowUp:{x:0,y:-step},ArrowDown:{x:0,y:step},ArrowLeft:{x:-step,y:0},ArrowRight:{x:step,y:0}}[key];
        if(delta) setNodes(ns=>ns.map(n=>n.selected? {...n,position:{x:n.position.x+delta.x,y:n.position.y+delta.y}}:n));
    },[getSelectedElements,setNodes,readOnly,disabled]);

    // ── Main keyboard handler ─────────────────────────────────────────────────
    useEffect(()=>{
        if(disabled) return;
        const onKeyDown=(e)=>{
            const cmdOrCtrl=isMac()? e.metaKey:e.ctrlKey;
            const target=e.target;
            if(target.tagName==='INPUT'||target.tagName==='TEXTAREA'||target.isContentEditable) return;

            if(e.key==='Delete'||e.key==='Backspace'){e.preventDefault();handleDelete();return;}
            if(cmdOrCtrl&&!e.shiftKey&&e.key==='z'){e.preventDefault();onUndo?.();return;}
            if((cmdOrCtrl&&e.shiftKey&&e.key==='z')||(cmdOrCtrl&&e.key==='y')){e.preventDefault();onRedo?.();return;}
            if(cmdOrCtrl&&e.key==='a'){e.preventDefault();handleSelectAll();return;}
            if(cmdOrCtrl&&!e.shiftKey&&e.key==='c'){handleCopy();return;}
            if(cmdOrCtrl&&!e.shiftKey&&e.key==='v'){e.preventDefault();handlePaste();return;}
            if(cmdOrCtrl&&e.key==='d'){e.preventDefault();handleDuplicate();return;}
            if(cmdOrCtrl&&!e.shiftKey&&e.key==='g'){e.preventDefault();onGroup?.();return;}
            if(cmdOrCtrl&&e.shiftKey&&e.key==='g'){e.preventDefault();onUngroup?.();return;}
            if(e.key==='+'||e.key==='='){e.preventDefault();handleZoom('in');return;}
            if(e.key==='-'){e.preventDefault();handleZoom('out');return;}
            if(['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)){e.preventDefault();handleArrowKey(e.key,e.shiftKey);return;}
        };
        window.addEventListener('keydown',onKeyDown);
        return ()=>window.removeEventListener('keydown',onKeyDown);
    },[disabled,handleDelete,handleCopy,handlePaste,handleDuplicate,handleSelectAll,handleZoom,handleArrowKey,onUndo,onRedo,onGroup,onUngroup]);

    return {handleDelete,handleCopy,handlePaste,handleDuplicate,handleSelectAll,handleZoom};
}
